import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Map, Handshake, CheckCircle2, ArrowRight, Upload, FileText } from 'lucide-react';
import { publicApi } from '../lib/api';
import { toast, Toaster } from 'sonner';

export function Partnerships() {
  const [form, setForm] = useState({
    name: '',
    entity_name: '',
    email: '',
    phone: '',
    total_available_capital: '',
    proof_of_funds_notes: '',
    notes: '',
  });
  const [pofFile, setPofFile] = useState(null);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        total_available_capital: parseFloat(String(form.total_available_capital).replace(/[,$]/g, '')) || 0,
      };
      // If POF file selected, send as FormData
      if (pofFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => formData.append(k, v));
        formData.append('pof_file', pofFile);
        await publicApi.applyPartnershipWithFile(formData);
      } else {
        await publicApi.applyPartnership(payload);
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center p-4" data-testid="partnerships-success">
        <Toaster position="top-right" />
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A2F23]">Application Received</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Thank you for your interest in partnering with us. Our team will review your application and reach out shortly.
            </p>
          </div>
          <a href="https://wefindland.com" className="inline-flex items-center gap-2 text-sm text-[#C68E5D] hover:underline">
            Visit our website <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F3]" data-testid="partnerships-page">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="bg-[#1A2F23] text-white">
        <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#C68E5D] rounded-sm flex items-center justify-center">
              <Map className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-bold text-lg">Land Finder</h2>
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold mb-3">Partner With Us</h1>
          <p className="text-white/70 text-sm sm:text-base max-w-xl">
            We're looking for qualified JV partners to participate in land investment opportunities.
            Submit your information below and our team will reach out to discuss the next steps.
          </p>
        </div>
        <div className="h-1 bg-[#C68E5D]"></div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <Card className="rounded-sm border shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Handshake className="w-5 h-5 text-[#C68E5D]" />
              <h2 className="text-lg font-semibold text-[#1A2F23]">Partnership Application</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal / Entity Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Full Name *</Label>
                  <Input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="John Smith"
                    className="mt-1.5 h-10 rounded-sm"
                    required
                    data-testid="partnership-name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entity Name</Label>
                  <Input
                    value={form.entity_name}
                    onChange={e => setForm(f => ({ ...f, entity_name: e.target.value }))}
                    placeholder="Smith Capital LLC"
                    className="mt-1.5 h-10 rounded-sm"
                    data-testid="partnership-entity"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Email Address *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="mt-1.5 h-10 rounded-sm"
                    required
                    data-testid="partnership-email"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Phone</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="mt-1.5 h-10 rounded-sm"
                    data-testid="partnership-phone"
                  />
                </div>
              </div>

              <Separator />

              {/* Financial Info */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Available Capital for Investment</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={form.total_available_capital}
                    onChange={e => setForm(f => ({ ...f, total_available_capital: e.target.value }))}
                    placeholder="250,000"
                    className="pl-7 h-10 rounded-sm font-mono"
                    data-testid="partnership-capital"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Proof of Funds</Label>
                <p className="text-[11px] text-muted-foreground mb-1.5">Upload a bank statement, line of credit, or financial letter. You can also provide it later in your portal.</p>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors ${pofFile ? 'border-green-300 bg-green-50' : 'border-muted-foreground/20 hover:border-[#C68E5D]/50'}`}
                  data-testid="partnership-pof-upload"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('File must be under 10MB');
                          return;
                        }
                        setPofFile(file);
                      }
                    }}
                  />
                  {pofFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">{pofFile.name}</span>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs ml-2" onClick={e => { e.stopPropagation(); setPofFile(null); }}>Remove</Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                      <p className="text-xs text-muted-foreground">Click to upload POF document <span className="text-[10px]">(optional)</span></p>
                      <p className="text-[10px] text-muted-foreground/60 mt-0.5">PDF, JPG, PNG, DOC — max 10MB</p>
                    </div>
                  )}
                </div>
                <textarea
                  value={form.proof_of_funds_notes}
                  onChange={e => setForm(f => ({ ...f, proof_of_funds_notes: e.target.value }))}
                  placeholder="Additional notes about your proof of funds..."
                  className="w-full border rounded-sm px-3 py-2 text-sm h-14 resize-none focus:outline-none focus:ring-2 focus:ring-ring mt-2"
                  data-testid="partnership-pof-notes"
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Additional Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Investment experience, preferred deal sizes, etc."
                  className="w-full border rounded-sm px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-ring mt-1.5"
                  data-testid="partnership-notes"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white rounded-sm text-sm font-semibold"
                data-testid="partnership-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>

              <p className="text-[11px] text-muted-foreground text-center">
                Your information is kept confidential and will only be used to evaluate partnership opportunities.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Partnerships;
